"use client";

import { useState } from 'react';
import { useRegionalFormatters } from '@/lib/use-regional-formatters';
import * as Icons from 'lucide-react';
import { ChevronDown } from 'lucide-react';

interface Objection {
  question: string;
  answer: string;
  icon: string;
}

interface ObjectionHandlingProps {
  config: {
    headline?: string;
    subheadline?: string;
    objections?: Objection[];
  };
}

export function ObjectionHandlingSection({ config }: ObjectionHandlingProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const objections = config.objections || [];

  return (
    <section className="py-24 px-4 bg-gradient-to-b from-white to-gray-50 dark:from-gray-950 dark:to-gray-900">
      <div className="container mx-auto max-w-4xl">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-black mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            {config.headline || 'Common Questions Answered'}
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400">
            {config.subheadline || 'Everything you need to know before getting started'}
          </p>
        </div>

        <div className="space-y-4">
          {objections.map((objection, index) => {
            const isOpen = openIndex === index;
            const IconComponent = (Icons as any)[objection.icon.split('-').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join('')] || Icons.HelpCircle;

            return (
              <div
                key={index}
                className="group bg-white dark:bg-gray-900 rounded-2xl border-2 border-gray-200 dark:border-gray-800 hover:border-blue-500 dark:hover:border-blue-500 transition-all duration-300 overflow-hidden"
              >
                <button
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  className="w-full p-6 flex items-start gap-4 text-left"
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center group-hover:bg-blue-200 dark:group-hover:bg-blue-900/50 transition-colors">
                    <IconComponent className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                      {objection.question}
                    </h3>
                  </div>
                  <ChevronDown
                    className={`flex-shrink-0 w-6 h-6 text-gray-400 transition-transform duration-300 ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                <div
                  className={`transition-all duration-300 ease-in-out ${
                    isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  } overflow-hidden`}
                >
                  <div className="px-6 pb-6 pl-22">
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                      {objection.answer}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
